import type { ItemSpec as Spec } from './itemSpec';

/**
 * 이사 짐 ItemPicker 의 "유사 항목 인접 배치"용 큐레이션 그룹.
 *
 * 배경: Spec 에는 분류(category)/정렬 순서 필드가 없고, 서버가 내려주는 spec
 * 목록 순서는 의미상 인접성을 보장하지 않는다. 그래서 클라이언트에서 이름 기반
 * 큐레이션 순서를 정의해 "세탁기 옆에 건조기", "책장 옆에 선반" 처럼 유사 항목이
 * 붙어 보이도록 정렬한다.
 *
 * 동작 원리:
 *   - 각 그룹은 "이름에 포함되면 그 그룹으로 분류"되는 키워드 배열이다.
 *   - 그룹은 위→아래 순서대로, 그룹 내부도 배열 순서대로 우선순위를 가진다.
 *   - 어떤 그룹 키워드와도 매칭되지 않는 spec(신규 추가 짐 등)은 맨 뒤로 밀리되,
 *     서버가 준 원래 순서를 그대로 유지한다 (큐레이션 맵 누락에 안전).
 *
 * 유지보수: 새 짐 spec 이 추가되면 적절한 그룹 키워드에 한 줄 넣으면 된다. 빠뜨려도
 * 동작은 깨지지 않고(맨 뒤 배치) 인접 정렬만 적용되지 않는다.
 */
const CURATED_GROUPS: readonly (readonly string[])[] = [
  // 거실 좌석/소파
  ['소파', '쇼파', '리클라이너', '안마의자'],
  // 영상/음향
  ['TV', '티비', '텔레비전', '스피커', '사운드바', '홈시어터', '프로젝터'],
  // 책상/사무
  ['책상', '컴퓨터', '데스크', '모니터'],
  // 의자
  ['의자', '게이밍'],
  // 책장/선반/수납 가구
  ['책장', '선반', '수납장', '진열장', '장식장', '서랍장', '서랍', '수납'],
  // 식탁/주방 가구
  ['식탁', '아일랜드', '주방'],
  // 침대/침실
  ['침대', '매트리스', '프레임', '화장대', '협탁'],
  // 옷장/드레스룸
  ['옷장', '드레스', '행거', '붙박이', '붙박이장'],
  // 냉장/주방 대형가전
  ['냉장고', '김치냉장고', '김치', '정수기'],
  // 세탁/건조
  ['세탁기', '건조기', '워시타워', '워시', '스타일러'],
  // 조리 가전
  [
    '인덕션',
    '가스레인지',
    '레인지',
    '전자레인지',
    '오븐',
    '에어프라이어',
    '식기세척기',
    '식기세척',
  ],
  // 공조/계절가전
  ['에어컨', '제습기', '공기청정기', '청정기', '선풍기', '서큘레이터', '히터', '난로', '온풍기'],
  // 운동기구
  ['러닝머신', '런닝머신', '실내자전거', '자전거', '운동기구', '홈트', '안마'],
  // 악기/대형 기타
  ['피아노', '디지털피아노', '악기'],
  // 생활/기타 가전
  ['청소기', '건조대', '빨래'],
  // 창호/패브릭
  ['커튼', '블라인드', '러그', '카페트', '카펫'],
  // 보관/박스
  ['금고', '캐리어', '박스', '수조', '어항'],
] as const;

/** 큐레이션 키워드를 평탄화한 우선순위 배열 (그룹 인접성을 그대로 보존). */
const CURATED_KEYWORD_ORDER: readonly string[] = CURATED_GROUPS.flatMap((g) => g);

/**
 * spec 이름 기준 큐레이션 우선순위.
 *
 * 가장 먼저 매칭되는 키워드의 평탄화 인덱스를 반환한다. 어떤 키워드와도 매칭되지
 * 않으면 `CURATED_KEYWORD_ORDER.length` (맨 뒤) 를 반환한다.
 */
export function curatedRank(name: string): number {
  for (let i = 0; i < CURATED_KEYWORD_ORDER.length; i++) {
    if (name.includes(CURATED_KEYWORD_ORDER[i])) return i;
  }
  return CURATED_KEYWORD_ORDER.length;
}

/**
 * spec 목록을 큐레이션 그룹 순서로 정렬한다 (안정 정렬).
 *
 * - 같은 그룹/키워드에 속한 항목끼리 인접하게 모인다.
 * - 큐레이션에 없는 항목은 맨 뒤로 가되, 입력(서버) 순서를 그대로 유지한다.
 * - 동일 rank 내에서도 입력 순서를 보존한다 (안정 정렬).
 */
export function sortSpecsByCuratedGroups<S extends Spec>(specs: S[]): S[] {
  return specs
    .map((spec, index) => ({ spec, index, rank: curatedRank(spec.name) }))
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index))
    .map((entry) => entry.spec);
}
